/**
 * Utilities for calculating rack dimensions and 3D positions
 */

import {
  RackPosition,
  RackDimensions,
  RackSummary,
  WarehouseDimensions,
  DEFAULT_RACK_DIMENSIONS
} from '../types/warehouseLayout';

// Realistic bin/section size defaults (in meters)
// Standard pallet rack section: ~2.7m wide per section of ~4-5 verticals
// Depth: ~1.0m for single-deep pallet racking
// Height per shelf level: ~0.5m (pallet height + clearance)
const DEFAULT_SECTION_WIDTH = 2.7;   // Width of a standard rack section (4-5 verticals)
const DEFAULT_VERTICALS_PER_SECTION = 4;
const DEFAULT_RACK_DEPTH = 1.1;      // Single-deep pallet rack depth
const DEFAULT_SHELF_HEIGHT = 0.5;    // Height per shelf level (incl. clearance)
const SHELF_THICKNESS = 0.03;
const RACK_FRAME_WIDTH = 0.08;

/**
 * Calculate rack dimensions based on rack summary.
 * Uses realistic warehouse pallet-rack proportions so racks fill the space naturally.
 */
export function calculateRackDimensions(
  rackSummary: RackSummary,
): RackDimensions {
  // Forsøk numerisk diff; fall tilbake til 1 hvis koder ikke er numeriske (f.eks. "SL", "V-A4")
  const vMin = Number.parseInt(rackSummary.minVertical ?? '', 10);
  const vMax = Number.parseInt(rackSummary.maxVertical ?? '', 10);
  const sMin = Number.parseInt(rackSummary.minShelf ?? '', 10);
  const sMax = Number.parseInt(rackSummary.maxShelf ?? '', 10);
  const verticalCount = Number.isFinite(vMin) && Number.isFinite(vMax) ? vMax - vMin + 1 : 1;
  const shelfCount = Number.isFinite(sMin) && Number.isFinite(sMax) ? sMax - sMin + 1 : 1;

  // Scale width based on number of vertical positions relative to a standard section
  const sections = Math.max(1, verticalCount / DEFAULT_VERTICALS_PER_SECTION);
  const width = sections * DEFAULT_SECTION_WIDTH + RACK_FRAME_WIDTH * 2;
  const depth = DEFAULT_RACK_DEPTH + RACK_FRAME_WIDTH * 2;
  const height = shelfCount * (DEFAULT_SHELF_HEIGHT + SHELF_THICKNESS) + SHELF_THICKNESS;

  return {
    width,
    depth,
    height,
    verticals: verticalCount,
    shelves: shelfCount,
    drawers: undefined,
  };
}

/**
 * Calculate 3D position for a rack based on its 2D position
 */
export function calculate3DRackPosition(
  rackPosition: RackPosition,
  rackDimensions: RackDimensions
): [number, number, number] {
  // Convert from 2D floor position to 3D world coordinates
  // Three.js uses Y as up axis, so we swap Y and Z
  const x = rackPosition.x;
  const y = rackDimensions.height / 2; // Center vertically
  const z = rackPosition.y;

  return [x, y, z];
}

/**
 * Calculate bin position within a rack (relative to rack origin)
 */
export function calculateBinPosition(
  vertical: number,
  shelf: number,
  rackDimensions: RackDimensions,
): [number, number, number] {
  // Derive bin size from rack dimensions and structure
  const binWidth = (rackDimensions.width - RACK_FRAME_WIDTH * 2) / rackDimensions.verticals;
  const binHeight = (rackDimensions.height - SHELF_THICKNESS) / rackDimensions.shelves - SHELF_THICKNESS;

  // Calculate position relative to rack center
  const totalWidth = rackDimensions.verticals * binWidth;
  const totalHeight = rackDimensions.shelves * (binHeight + SHELF_THICKNESS);

  const x = (vertical - 0.5) * binWidth - totalWidth / 2;
  const y = (shelf - 0.5) * (binHeight + SHELF_THICKNESS) - totalHeight / 2 + binHeight / 2;
  const z = 0;

  return [x, y, z];
}

/**
 * Apply rotation to rack dimensions (swap width/depth for 90° or 270°)
 */
export function getRotatedDimensions(
  dimensions: RackDimensions,
  rotation: 0 | 90 | 180 | 270
): { width: number; depth: number } {
  if (rotation === 90 || rotation === 270) {
    return { width: dimensions.depth, depth: dimensions.width };
  }
  return { width: dimensions.width, depth: dimensions.depth };
}

/**
 * Snap position to grid
 */
export function snapToGrid(value: number, gridUnit: number): number {
  return Math.round(value / gridUnit) * gridUnit;
}

/**
 * Check if a rack position is within warehouse bounds
 */
export function isWithinBounds(
  position: RackPosition,
  dimensions: RackDimensions,
  warehouseDimensions: WarehouseDimensions
): boolean {
  const rotated = getRotatedDimensions(dimensions, position.rotation);
  const halfWidth = rotated.width / 2;
  const halfDepth = rotated.depth / 2;

  return (
    position.x - halfWidth >= 0 &&
    position.x + halfWidth <= warehouseDimensions.length &&
    position.y - halfDepth >= 0 &&
    position.y + halfDepth <= warehouseDimensions.width
  );
}

/**
 * Check if two racks overlap
 */
export function racksOverlap(
  rack1: RackPosition,
  dim1: RackDimensions,
  rack2: RackPosition,
  dim2: RackDimensions,
  margin: number = 0.5 // Minimum gap between racks
): boolean {
  const rotated1 = getRotatedDimensions(dim1, rack1.rotation);
  const rotated2 = getRotatedDimensions(dim2, rack2.rotation);

  const halfW1 = rotated1.width / 2 + margin / 2;
  const halfD1 = rotated1.depth / 2 + margin / 2;
  const halfW2 = rotated2.width / 2 + margin / 2;
  const halfD2 = rotated2.depth / 2 + margin / 2;

  const overlapX = Math.abs(rack1.x - rack2.x) < (halfW1 + halfW2);
  const overlapY = Math.abs(rack1.y - rack2.y) < (halfD1 + halfD2);

  return overlapX && overlapY;
}

/**
 * Find optimal position for a new rack (avoid overlaps)
 */
export function findOptimalPosition(
  existingRacks: RackPosition[],
  existingDimensions: Map<string, RackDimensions>,
  newDimensions: RackDimensions,
  warehouseDimensions: WarehouseDimensions,
  gridUnit: number
): { x: number; y: number } | null {
  const rotated = getRotatedDimensions(newDimensions, 0);
  const startX = rotated.width / 2 + 1;
  const startY = rotated.depth / 2 + 1;

  // Try positions in a grid pattern — step size adapts to rack size for efficiency
  const stepX = Math.max(gridUnit * 2, rotated.width * 0.5);
  const stepY = Math.max(gridUnit * 2, rotated.depth * 0.5);
  for (let y = startY; y < warehouseDimensions.width - rotated.depth / 2; y += stepY) {
    for (let x = startX; x < warehouseDimensions.length - rotated.width / 2; x += stepX) {
      const testPosition: RackPosition = {
        rackNumber: 'test',
        x: snapToGrid(x, gridUnit),
        y: snapToGrid(y, gridUnit),
        rotation: 0
      };

      let hasCollision = false;
      for (const existingRack of existingRacks) {
        const existingDim = existingDimensions.get(existingRack.rackNumber) || DEFAULT_RACK_DIMENSIONS;
        if (racksOverlap(testPosition, newDimensions, existingRack, existingDim)) {
          hasCollision = true;
          break;
        }
      }

      if (!hasCollision) {
        return { x: testPosition.x, y: testPosition.y };
      }
    }
  }

  return null;
}

/**
 * Calculate distance between two points
 */
export function calculateDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * Convert screen coordinates to world coordinates
 * Note: Must match the scale calculation in Layout2DCanvas draw() function
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  canvasWidth: number,
  canvasHeight: number,
  warehouseDimensions: WarehouseDimensions,
  zoom: number,
  panOffset: { x: number; y: number }
): { x: number; y: number } {
  // Use same padding as draw function (40px margin)
  const scale = Math.min(
    (canvasWidth - 40) / warehouseDimensions.length,
    (canvasHeight - 40) / warehouseDimensions.width
  ) * zoom;

  const offsetX = (canvasWidth - warehouseDimensions.length * scale) / 2 + panOffset.x;
  const offsetY = (canvasHeight - warehouseDimensions.width * scale) / 2 + panOffset.y;

  return {
    x: (screenX - offsetX) / scale,
    y: (screenY - offsetY) / scale
  };
}

/**
 * Convert world coordinates to screen coordinates
 * Note: Must match the scale calculation in Layout2DCanvas draw() function
 */
export function worldToScreen(
  worldX: number,
  worldY: number,
  canvasWidth: number,
  canvasHeight: number,
  warehouseDimensions: WarehouseDimensions,
  zoom: number,
  panOffset: { x: number; y: number }
): { x: number; y: number } {
  // Use same padding as draw function (40px margin)
  const scale = Math.min(
    (canvasWidth - 40) / warehouseDimensions.length,
    (canvasHeight - 40) / warehouseDimensions.width
  ) * zoom;

  const offsetX = (canvasWidth - warehouseDimensions.length * scale) / 2 + panOffset.x;
  const offsetY = (canvasHeight - warehouseDimensions.width * scale) / 2 + panOffset.y;

  return {
    x: worldX * scale + offsetX,
    y: worldY * scale + offsetY
  };
}
